import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useCanvasStore } from '@modules/canvas/store';
import { useCanvasZoom } from '@/modules/canvas/hooks';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { isGeneratedLayerNames, LayerNames } from '@/modules/common/types/layers';
import { useUserPreference } from '@/modules/userPreferences/hooks';
import { UserPreferenceName } from '@/modules/userPreferences';
import { useFloorplanDesignStore } from '@modules/floorplanDesign/store/useFloorplanDesignStore';
import { useLayerStore } from '@/modules/layers/store/useLayerStore';
import { getChildLayers } from '@/modules/layers/layerHierarchy';
import { getEffectiveLayerVisibility } from '@/modules/layers/visibility';
import { buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers';

import { ElementWithLayerTags } from './types';
import { FDDM_LAYER_DEFINITIONS } from '../layers/layerTree';

type ElementLayerMap = Map<string, Set<LayerNames>>;

// Vehicle-generated layers have their own per-vehicle visibility state and are
// synchronized by CanvasService and useLayerStore. The shared LoD pass must not
// overwrite that state with the fallback visibility of the base layer.
const SHARED_LAYER_DEFINITIONS = FDDM_LAYER_DEFINITIONS.filter(
  (definition) => !isGeneratedLayerNames(definition.name),
);

const LAYER_TAG_MATCHERS = SHARED_LAYER_DEFINITIONS.flatMap((definition) =>
  definition.fddmTags.map((tag) => ({
    layerName: definition.name,
    matcher: buildTagMatchRegExp([tag]),
  })),
);

const addElementLayer = (
  elementLayerMap: ElementLayerMap,
  elementId: string,
  layerName: LayerNames,
): void => {
  const existingLayers = elementLayerMap.get(elementId);

  if (existingLayers) {
    existingLayers.add(layerName);
    return;
  }

  elementLayerMap.set(elementId, new Set([layerName]));
};

const removeAncestorLayers = (layerNames: Set<LayerNames>): Set<LayerNames> => {
  const ancestorLayers = new Set<LayerNames>();

  layerNames.forEach((layerName) => {
    const childLayers = getChildLayers(layerName);

    if (childLayers.some((childLayer) => layerNames.has(childLayer))) {
      ancestorLayers.add(layerName);
    }
  });

  return new Set([...layerNames].filter((layerName) => !ancestorLayers.has(layerName)));
};

export const useLoD = (): void => {
  const canvas = useCanvasStore((state) => state.instance);
  const nativeZoomToUserZoomFactor = useCanvasStore((state) => state.nativeZoomToUserZoomFactor);
  const hasTracks = useFloorplanDesignStore((state) => Boolean(state.tracks?.length));
  const activeModule = useAppStore((state) => state.activeModule);
  const isCanvasViewerActive = activeModule === 'canvas_viewer';
  const { canvasNativeZoomLevel } = useCanvasZoom();

  const { layers, invisibleElements } = useLayerStore(
    useShallow((state) => ({
      layers: state.layers,
      invisibleElements: state.invisibleElements,
    })),
  );

  const tracksZoomThreshold = useUserPreference(UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD);
  const boxesZoomThreshold = useUserPreference(UserPreferenceName.LOD_BOXES_ZOOM_THRESHOLD);
  const vehicleLayoutsZoomThreshold = useUserPreference(
    UserPreferenceName.LOD_VEHICLE_LAYOUTS_ZOOM_THRESHOLD,
  );
  const bubblesZoomThreshold = useUserPreference(UserPreferenceName.LOD_BUBBLES_ZOOM_THRESHOLD);

  const preferenceValues = useMemo<Partial<Record<UserPreferenceName, number>>>(
    () => ({
      [UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD]: tracksZoomThreshold,
      [UserPreferenceName.LOD_BOXES_ZOOM_THRESHOLD]: boxesZoomThreshold,
      [UserPreferenceName.LOD_VEHICLE_LAYOUTS_ZOOM_THRESHOLD]: vehicleLayoutsZoomThreshold,
      [UserPreferenceName.LOD_BUBBLES_ZOOM_THRESHOLD]: bubblesZoomThreshold,
    }),
    [
      tracksZoomThreshold,
      boxesZoomThreshold,
      vehicleLayoutsZoomThreshold,
      bubblesZoomThreshold,
    ],
  );

  const toNativeZoom = useCallback(
    (displayZoom: number): number => {
      if (nativeZoomToUserZoomFactor <= 0) return displayZoom;

      return displayZoom / nativeZoomToUserZoomFactor;
    },
    [nativeZoomToUserZoomFactor],
  );

  const getNativeZoomPreferenceValue = useCallback(
    (preference?: UserPreferenceName): number | undefined => {
      if (!preference) return undefined;

      const displayZoom = preferenceValues[preference];

      if (displayZoom === undefined) return undefined;

      return toNativeZoom(displayZoom);
    },
    [preferenceValues, toNativeZoom],
  );

  const getNativeHideFromZoomPreferenceValue = useCallback(
    (preference?: UserPreferenceName): number | undefined => {
      if (!preference) return undefined;

      if (preference === UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD && !hasTracks) {
        return undefined;
      }

      return getNativeZoomPreferenceValue(preference);
    },
    [getNativeZoomPreferenceValue, hasTracks],
  );

  /*
   * Zoom changes every frame, but layer visibility only changes when a zoom
   * threshold is crossed. This key remains identical between thresholds.
   */
  const effectiveLayerVisibilityKey = SHARED_LAYER_DEFINITIONS.map((definition) => {
    const layer = layers[definition.name];
    const showFromZoom = getNativeZoomPreferenceValue(definition.lod?.showFromZoomPreference);
    const hideFromZoom = getNativeHideFromZoomPreferenceValue(
      definition.lod?.hideFromZoomPreference,
    );

    const isVisible = getEffectiveLayerVisibility({
      visibilityMode: layer.visibilityMode,
      parentVisible: true,
      zoom: canvasNativeZoomLevel,
      showFromZoom,
      hideFromZoom,
    });

    return Number(isVisible);
  }).join('');

  /*
   * This object is now stable while zooming inside the same threshold range.
   * Consequently, syncLayerVisibility and its effect are also stable.
   */
  const effectiveLayerVisibility = useMemo<Partial<Record<LayerNames, boolean>>>(() => {
    const visibilityByLayer: Partial<Record<LayerNames, boolean>> = {};

    SHARED_LAYER_DEFINITIONS.forEach((definition, index) => {
      visibilityByLayer[definition.name] = effectiveLayerVisibilityKey[index] === '1';
    });

    return visibilityByLayer;
  }, [effectiveLayerVisibilityKey]);

  const collectElementLayerMap = useCallback((): ElementLayerMap => {
    const elementLayerMap: ElementLayerMap = new Map();

    if (!canvas) return elementLayerMap;

    LAYER_TAG_MATCHERS.forEach(({ layerName, matcher }) => {
      const elements = canvas.getElementsByTag(matcher);

      elements.forEach((element: ElementWithLayerTags) => {
        addElementLayer(elementLayerMap, element.id, layerName);
      });
    });

    elementLayerMap.forEach((layerNames, elementId) => {
      elementLayerMap.set(elementId, removeAncestorLayers(layerNames));
    });

    return elementLayerMap;
  }, [canvas]);

  const isElementVisibleByLayers = useCallback(
    (layerNames: Set<LayerNames>): boolean => {
      let isVisible = true;

      layerNames.forEach((layerName) => {
        if (effectiveLayerVisibility[layerName] === false) {
          isVisible = false;
        }
      });

      return isVisible;
    },
    [effectiveLayerVisibility],
  );

  const syncLayerVisibility = useCallback((): void => {
    if (!canvas) return;

    const elementLayerMap = collectElementLayerMap();
    const idsToShow: string[] = [];
    const idsToHide: string[] = [];

    elementLayerMap.forEach((layerNames, elementId) => {
      const visible = isElementVisibleByLayers(layerNames) && !invisibleElements.has(elementId);

      if (visible) {
        idsToShow.push(elementId);
        return;
      }

      idsToHide.push(elementId);
    });

    if (idsToShow.length > 0) {
      canvas.setElementVisibility(idsToShow, true, true);
    }

    if (idsToHide.length > 0) {
      canvas.setElementVisibility(idsToHide, false, true);
    }
  }, [canvas, collectElementLayerMap, invisibleElements, isElementVisibleByLayers]);

  useEffect(() => {
    if (!canvas) return;
    if (isCanvasViewerActive) return;

    syncLayerVisibility();
  }, [canvas, isCanvasViewerActive, syncLayerVisibility]);
};
